import { useEffect, useState } from "react";
import { useStateUrlParamsBoolean } from "../common/hooks/useStateUrlParams";
import { useLocalStorageTab } from "@common/hooks/useLocalStorageTab";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "../localization/useTranslation";
import { LegalEntityModel } from "./addLegalEntityDialog/AddLegalEntityDialog.types";
import { useUrlProvider } from "../useUrlProvider";
import { SharingBaseTableViewModel } from "../processingActivity/components/sharingsTable/SharingsTable.types";
import { ILegalEntitiesProps, LegalEntityScope, toLegalEntityScope } from "./legalEntities/LegalEntities.types";
import { UserPermissions } from "../auth/userContextProvider/UserContextProvider.types";
import { exhaustiveGuard } from "../common/utilities";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import { useDotLegalQueryClient, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { LegalEntityAuditTabs, LegalEntityPlanTabId, LegalEntityTabCountsViewModel } from "./LegalEntity.types";
import { useOverviewTopTab } from "@common/hooks/useOverviewTopTab";

export function getLegalEntityQueryKey(id: string, legalEntityScope?: number) {
    return "legalEntity" + id + (legalEntityScope ?? "");
}

export function getLegalEntityTabCountsQueryKey(id: string) {
    return "legalEntityTabCounts" + id;
}

export function useLegalEntityHook(props: ILegalEntitiesProps) {
    const { customerId } = useUserContext();

    const [selectedTab, setSelectedTab] = useLocalStorageTab(`legal-entity-plan-tab-${customerId}`, legalEntityPlanTabMapping, LegalEntityPlanTabId.Documents);
    const { translateCountry } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { getLegalEntityUrl, getGroupEntityUrl } = useUrlProvider();
    const [showChangeCustomerOwnedDialog, setShowChangeCustomerOwnedDialog] = useState(false);
    const [showThirdCountryDialog, setShowThirdCountryDialog] = useState(false);
    const [showAuditLogDialog, setShowAuditLogDialog] = useStateUrlParamsBoolean("auditlog", false);
    // The id of the data processor or sub processor type whose use is being explained, if any.
    const [showLegalEntityTypeUsageDialog, setShowLegalEntityTypeUsageDialog] = useState<string | null>(null);

    const navigate = useNavigate();
    const queryClient = useDotLegalQueryClient();
    const queryKey = getLegalEntityQueryKey(id!, props.legalEntityScope);

    let legalEntityQuery = useDotLegalQueryWithKey<LegalEntityModel>(queryKey, `/LegalEntity/${id}`);
    let sharingsQuery = useDotLegalQueryWithKey<SharingBaseTableViewModel>("legalEntitySharing" + id, `/LegalEntity/privacySharings/${id}`);
    const tabCountsQueryKey = getLegalEntityTabCountsQueryKey(id!);
    let tabCountsQuery = useDotLegalQueryWithKey<LegalEntityTabCountsViewModel>(tabCountsQueryKey, `/LegalEntity/${id}/tab-counts`);

    // Ownership decides which of the two detail urls an entity lives on, so a link to the wrong one - a group
    // entity opened as external, or an entity that has since changed hands - is corrected rather than 404'd.
    useEffect(() => {
        if (legalEntityQuery.data) {
            const scope = toLegalEntityScope(legalEntityQuery.data.isCustomerOwned);

            if (scope !== props.legalEntityScope) {
                switch (scope) {
                    case LegalEntityScope.External:
                        navigate(getLegalEntityUrl(id!, selectedTab!), { replace: true });
                        break;
                    case LegalEntityScope.GroupEntity:
                        navigate(getGroupEntityUrl(id!, selectedTab!), { replace: true });
                        break;
                    default:
                        exhaustiveGuard(scope);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [legalEntityQuery.data?.isCustomerOwned]);

    let legalEntityLoading = legalEntityQuery.isLoading;
    let sharingsLoading = sharingsQuery.isLoading;

    let legalEntityData;
    if (legalEntityQuery.data) {
        legalEntityData = {
            ...legalEntityQuery.data,
            country: translateCountry(legalEntityQuery.data.country),
            countryCode: legalEntityQuery.data.country,
            zipCodeAndCity: (legalEntityQuery.data.zipCode ?? "") + " " + (legalEntityQuery.data.city ?? ""),
        };
    }

    const { selectedTopTab, setSelectedTopTab } = useOverviewTopTab(
        "legal-entity-info-tab",
        "allLegalEntityInfo",
        legalEntityQuery.data?.entityTemplateId,
        legalEntityQuery.data?.templateSteps
    );

    const refecthLegalEntity = () => {
        queryClient.refetchQueries(queryKey);
    };

    const refetchTabCounts = () => {
        queryClient.refetchQueries(tabCountsQueryKey);
    };

    return {
        legalEntityLoading,
        legalEntityData,
        sharingsLoading,
        sharingsData: sharingsQuery.data,
        tabCounts: tabCountsQuery.data,
        refecthLegalEntity,
        refetchTabCounts,
        selectedTab,
        setSelectedTab,
        selectedTopTab,
        setSelectedTopTab,
        showChangeCustomerOwnedDialog,
        setShowChangeCustomerOwnedDialog,
        showThirdCountryDialog,
        setShowThirdCountryDialog,
        legalEntityId: id,
        showLegalEntityTypeUsageDialog,
        setShowLegalEntityTypeUsageDialog,
        showAuditLogDialog,
        setShowAuditLogDialog,
    };
}

const legalEntityPlanTabMapping: Record<number, LegalEntityPlanTabId> = {
    [LegalEntityAuditTabs.Contracts]: LegalEntityPlanTabId.Contracts,
    [LegalEntityAuditTabs.Classifications]: LegalEntityPlanTabId.Classifications,
    [LegalEntityAuditTabs.Assets]: LegalEntityPlanTabId.Assets,
    [LegalEntityAuditTabs.Sharings]: LegalEntityPlanTabId.Sharings,
    [LegalEntityAuditTabs.Audits]: LegalEntityPlanTabId.Audits,
    [LegalEntityAuditTabs.Documents]: LegalEntityPlanTabId.Documents,
    [LegalEntityAuditTabs.Contacts]: LegalEntityPlanTabId.Contacts,
    [LegalEntityAuditTabs.Tasks]: LegalEntityPlanTabId.Tasks,
};

export function getLegalEntityPermissions(permissions: UserPermissions, isGroupEntity: boolean) {
    if (isGroupEntity) {
        return {
            read: permissions.canManageGroupEntities,
            create: permissions.canManageGroupEntities,
            edit: permissions.canManageGroupEntities,
            delete: permissions.canManageGroupEntities,
        };
    } else {
        return {
            read: permissions.companyPermissions.read,
            create: permissions.companyPermissions.create,
            edit: permissions.companyPermissions.edit,
            delete: permissions.companyPermissions.delete,
        };
    }
}
